import { Icon } from "./Icon";
import { Select } from "./Field";

/** One option a ChipPicker can show. `selectable: false` keeps it out of the
 *  add list while still rendering as a chip when already chosen — how a
 *  retired option stops being offered without stranding what carries it. */
export interface ChipOption {
  id: string;
  name: string;
  /** Optgroup heading. Omitted or empty sorts last, under "Other". */
  group?: string;
  selectable: boolean;
}

/** Options bucketed by group, keeping list order; the empty group sorts last. */
export function groupOptions<T extends { group?: string }>(options: T[]): [string, T[]][] {
  const groups = [...new Set(options.map((o) => o.group ?? ""))].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : 0));
  return groups.map((g) => [g, options.filter((o) => (o.group ?? "") === g)]);
}

/** One option as a small chip; `onRemove` adds the × control. */
export function Chip({ label, removeLabel, onRemove, disabled }: { label: string; removeLabel?: string; onRemove?: () => void; disabled?: boolean }) {
  return (
    <span className="inline-flex h-6 items-center gap-1 rounded-full border border-[var(--border)] bg-gray-100 pl-2 pr-1 text-xs text-gray-800">
      {label}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeLabel}
          className="rounded-full p-0.5 text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring disabled:opacity-50"
        >
          <Icon name="x" />
        </button>
      ) : (
        <span className="w-1" />
      )}
    </span>
  );
}

/**
 * The chosen options as chips, plus a select to add one more.
 *
 * The whole set is posted on every change, not a delta — the repos that back
 * this replace-and-diff rather than patch. Unselectable options are never
 * offered but still render when already chosen, so retiring one never leaves
 * a record that cannot be saved.
 */
export function ChipPicker({
  id,
  options,
  value,
  onChange,
  disabled,
  addLabel,
  removeLabel,
  placeholder,
  emptyLabel,
}: {
  id: string;
  /** Every option; the picker filters the unselectable ones itself. */
  options: ChipOption[];
  /** Chosen option ids. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Accessible name of the add control, e.g. "Add tag". */
  addLabel: string;
  /** Accessible name of a chip's × control, e.g. `Remove tag ${name}`. */
  removeLabel: (name: string) => string;
  /** Prompt option while something is still addable. */
  placeholder: string;
  /** Prompt option once nothing is left to add. */
  emptyLabel: string;
}) {
  const byId = new Map(options.map((o) => [o.id, o]));
  const chosen = value.map((v) => byId.get(v)).filter((o): o is ChipOption => o !== undefined);
  const offered = options.filter((o) => o.selectable && !value.includes(o.id));

  return (
    <div className="flex flex-col gap-2">
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((o) => (
            <Chip
              key={o.id}
              label={o.name}
              removeLabel={removeLabel(o.name)}
              disabled={disabled}
              onRemove={() => onChange(value.filter((v) => v !== o.id))}
            />
          ))}
        </div>
      )}
      <Select
        id={id}
        aria-label={addLabel}
        value=""
        disabled={disabled || offered.length === 0}
        onChange={(e) => {
          if (e.target.value) onChange([...value, e.target.value]);
        }}
      >
        <option value="">{offered.length === 0 ? emptyLabel : placeholder}</option>
        {groupOptions(offered).map(([group, list]) => (
          <optgroup key={group || "—"} label={group || "Other"}>
            {list.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
    </div>
  );
}
